import { useOutletContext } from 'react-router-dom'

import { Box, Button, Text, useUnsafeMemo } from '@factorialco/deprecated-design-system'
import { F0Alert } from '@factorialco/f0-react'
import { TypedDocumentNode } from '@graphql-typed-document-node/core'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import StructuredCostCenterPicker from 'modules/cost_centers/components/CostCenterAssigments/components/AssignmentsManager/Picker/StructuredPicker'
import { formatAssignments } from 'modules/cost_centers/components/CostCenterAssigments/lib/formatter'
import {
  CostCenterAssignment,
  FormattedAssignment,
  FormattedAssignmentWithDeletion,
} from 'modules/cost_centers/components/CostCenterAssigments/lib/types'

import CostCenterPicker from './Picker'
import { DateSelectors } from './components/DateSelectors'
import { DeleteButton } from './components/DeleteButton'
import { UpdateButton } from './components/UpdateButton'
import { useAssignmentsManager } from './hooks/useAssignmentsManager'
import moment from 'moment'

interface Props {
  allAssignments?: CostCenterAssignment[]
  assignments?: CostCenterAssignment[]
  resourceId: string
  resourceType: string
  title: string
  description: string
  isCreating?: boolean
  customSaveButton?: (
    assignmentsToCreate: FormattedAssignment[],
    assignmentsToUpdate: FormattedAssignmentWithDeletion[],
    disabled: boolean
  ) => React.ReactNode
  onCancel?: () => void
  onDelete?: () => void
  onSave?: () => void
  refetchQueries?: TypedDocumentNode[]
  deleteDisabled?: boolean
}

export const AssignmentsManager = ({
  allAssignments = [],
  assignments = [],
  resourceId,
  resourceType,
  title,
  description,
  isCreating = false,
  customSaveButton,
  onCancel,
  onDelete,
  onSave,
  refetchQueries,
  deleteDisabled = false,
}: Props) => {
  const { hasNewStructureEnabled } = useOutletContext<{
    hasNewStructureEnabled: boolean
  }>()

  const {
    assignmentsToCreate,
    assignmentsToUpdate,
    selectedDateRange,
    assignments: validAssignments,
    validForSave,
    validations,

    // Legacy mode functions
    addNewAssignment,
    deleteValue,
    updateAssignment,
    // Structured mode functions
    addNewStructuredAssignment,
    deleteStructuredAssignment,
    updateCostCenter,
    updateStructuredPercentage,
    // Shared functions
    setNewDateRange,
  } = useAssignmentsManager({
    currentAssignments: formatAssignments(assignments, hasNewStructureEnabled),
    allAssignments,
    hasNewStructureEnabled,
    isCreating,
  })
  const assignmentsIds = assignments.map((assignment) => assignment.id)

  // Show empty state for structured mode when employee has no existing assignments
  const showEmptyState = hasNewStructureEnabled && allAssignments.length === 0

  const isCurrent = useUnsafeMemo(() => {
    const startDate = assignments[0]?.startDate
    const endDate = assignments[0]?.endDate
    const startedAt = moment(startDate)
    const endedAt = endDate ? moment(endDate) : null

    const startedBeforeToday = startedAt.isSameOrBefore(moment(), 'day')
    const endedIsOrAfterToday = endedAt === null || endedAt?.isSameOrAfter(moment(), 'day')

    return startedBeforeToday && endedIsOrAfterToday
  }, [assignments])

  // Early return for empty state - render alert without the card container
  if (showEmptyState) {
    return (
      <F0Alert
        title={i18n.t('finance.cost_centers.assignments.structured.no_assignment.title')}
        description={i18n.t(
          'finance.cost_centers.assignments.structured.no_assignment.description'
        )}
        variant="info"
      />
    )
  }

  return (
    <Box paddingX="s24" paddingY="s24" gap="s24">
      <Box gap="s8">
        <Text.Normal weight="medium" size="300" color="grey1000">
          {hasNewStructureEnabled
            ? i18n.t('finance.cost_centers.assignments.structured.title')
            : title}
        </Text.Normal>

        <Text.Normal weight="normal" size="300" color="textSecondary">
          {hasNewStructureEnabled
            ? i18n.t('finance.cost_centers.assignments.structured.description')
            : description}
        </Text.Normal>
      </Box>

      {hasNewStructureEnabled ? (
        <StructuredCostCenterPicker
          assignments={validAssignments}
          addNewAssignment={addNewStructuredAssignment}
          onCostCenterChange={updateCostCenter}
          onPercentageChange={updateStructuredPercentage}
          onDelete={deleteStructuredAssignment}
          employeeId={resourceType === 'Employee' ? resourceId : undefined}
          hideDelete={hasNewStructureEnabled}
        />
      ) : (
        <CostCenterPicker
          assignments={validAssignments}
          updateAssignment={updateAssignment}
          deleteValue={deleteValue}
          addNewAssignment={addNewAssignment}
        />
      )}

      <DateSelectors
        hasNewStructureEnabled={hasNewStructureEnabled}
        isCreating={isCreating}
        isCurrent={isCurrent}
        error={validations.overlappedAssignments}
        startDateError={validations.startDateInPast}
        startDate={selectedDateRange.startDate}
        endDate={selectedDateRange.endDate}
        handleSelectedDatesChange={setNewDateRange}
        canBeMarkedAsActive={validations.canBeMarkedAsActive}
      />

      <Box flexDirection="row" gap="s8" paddingTop="s16" justifyContent="spaceBetween">
        {onDelete && (
          <DeleteButton
            ids={assignmentsIds}
            onDelete={onDelete}
            refetchQueries={refetchQueries}
            disabled={deleteDisabled}
          />
        )}

        <Box flexDirection="row" gap="s8" justifyContent="flexEnd" width="full">
          {onCancel && (
            <Button type="secondary" onClick={onCancel}>
              {i18n.t('common.cancel')}
            </Button>
          )}

          {customSaveButton ? (
            customSaveButton(assignmentsToCreate, assignmentsToUpdate, !validForSave)
          ) : (
            <UpdateButton
              refetchQueries={refetchQueries}
              assignmentsToCreate={assignmentsToCreate}
              assignmentsToUpdate={assignmentsToUpdate}
              resourceId={resourceId}
              resourceType={resourceType}
              onSave={() => {
                track(trackingEvents.accounting.saveCostCenterAssignment, {
                  source: 'edit_assignment',
                })

                onSave?.()
              }}
              disabled={!validForSave}
            />
          )}
        </Box>
      </Box>
    </Box>
  )
}
